import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys, type QueryKeyPart } from "@nasam/kernel";
import type {
  ArchivedAssetsListQuery,
  AssetCreateInput,
  AssetsListQuery,
  AssetsRepository,
  AssetUpdateInput,
} from "../domain";

function params(
  query: AssetsListQuery | ArchivedAssetsListQuery | undefined,
): Readonly<Record<string, QueryKeyPart>> | undefined {
  return query === undefined ? undefined : { ...query };
}

export function useAssetsQuery(
  repository: AssetsRepository,
  query?: AssetsListQuery,
) {
  return useQuery({
    queryKey: queryKeys.assets.list(params(query)),
    queryFn: () => repository.list(query),
  });
}

export function useCreateAssetMutation(repository: AssetsRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AssetCreateInput) => repository.create(input),
    // An archived match created nothing, so only a real creation touches the list.
    onSuccess: async (result) => {
      if (result.status === "created") {
        await client.invalidateQueries({ queryKey: queryKeys.assets.all });
      }
    },
  });
}

export function useAssetCreateFormQuery(
  repository: AssetsRepository,
  assetTypeId: string,
) {
  return useQuery({
    queryKey: [...queryKeys.assets.all, "create-form", assetTypeId],
    queryFn: () => repository.getCreateForm(assetTypeId),
    enabled: assetTypeId.length > 0,
  });
}

export function useAssetQuery(repository: AssetsRepository, id: string) {
  return useQuery({
    queryKey: queryKeys.assets.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}

export function useUpdateAssetMutation(repository: AssetsRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AssetUpdateInput }) =>
      repository.update(id, input),
    onSuccess: async (_result, variables) => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: queryKeys.assets.detail(variables.id),
        }),
        client.invalidateQueries({ queryKey: queryKeys.assets.lists() }),
      ]);
    },
  });
}

export function useDeleteAssetMutation(repository: AssetsRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repository.delete(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.assets.all }),
  });
}

export function useRestoreAssetMutation(repository: AssetsRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repository.restore(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.assets.all }),
  });
}

export function useArchivedAssetsQuery(
  repository: AssetsRepository,
  query?: ArchivedAssetsListQuery,
) {
  return useQuery({
    // Nested under assets.lists() so a restore's `assets.all` invalidation
    // also refetches this list (the restored row drops out).
    queryKey: [...queryKeys.assets.lists(), "archived", params(query) ?? {}],
    queryFn: () => repository.listArchived(query),
  });
}
